/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output} from '@angular/core';
import {Observable} from 'rxjs';

import {ModalService} from './modal.service';

/**
 * The different states a modal can be in
 */
export enum ModalState {
  // The default state
  NONE,
  // State when modal is waiting for a callback response (on cancel, on submit)
  WAITING,
  // Valid state : the async operation associated with this modal has been successful, modal is now ready to be closed
  VALID,
  // Invalid state : the async operation associated with this modal has failed, errors needs to be fixed
  INVALID
}

/**
 * Commands that can be passed to the modal to change its state and display messages
 */
export interface ModalCommand {
  state: ModalState;
  message: string;
  duration?: number;
}

/**
 * Component that scaffolds the inner workings of modals. It ease the creation of various types of modals
 *
 * Example usages :
 * ----------------
 *
 *
 * Form modal with cancel and submit buttons :
 *
 * ```
 * <gp-modal [onSubmit]="modalSubmitted()">
 *     <modal-title>My modal title</modal-title>
 *     <modal-body>My modal body</modal-body>
 * </gp-modal>
 * ```
 *
 * The modalSubmitted callback should be a promise of value. When/if it resolves the promise, the modal is
 * automatically closed ; when/if the promise is rejected, the modal stays open (typical use case would be to perform
 * some asynchronous validation).
 *
 * Modal with custom footer (default cancel and submit buttons are not displayed) :
 *
 * ```
 * <gp-modal>
 *     <modal-title>My modal title</modal-title>
 *     <modal-body>My modal body</modal-body>
 *     <modal-footer>My modal footer</modal-footer>
 * </gp-modal>
 * ```
 *
 * All inputs :
 *
 * * ```[closable]``` whether or not the modal should have a cross button on the top right that lets the user close it
 *                    (default: ```false```)
 * * ```[form]``` whether or not the modal holds a form and has footer buttons (when not provided with a custom footer)
 *                default: true
 * * ```[cancellable]``` for form modals only (does not affect modals with custom footer) ; whether or not the modal
 *                       offers a cancel button that let the user cancel the action and close the modal
 *                       (default: ```true```)
 * * ```[submittable]``` for form modals only (does not affect modals with custom footer) ; whether or not the modal
 *                       offers a submit button that let the user submit the form to perform an action.
 *                       See ```[onSubmit]```
 * * ```[onSubmit]``` for submittable modals, the callback to call on submit. Must return a promise. If rejected, modal
 *                    stays open. Default : promise that resolve immediately
 * * ```[onCancel]``` for cancellable modals, the callback to call on submit. Must return a promise. If rejected, modal
 *                    stays open. Default : promise that resolve immediately
 *
 */
@Component({
  selector: 'gp-modal',
  styleUrls: ['./modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="modal in" style="display: block; overflow: scroll">
      <div class="modal-dialog" [ngClass]="{'modal-lg': size === 'lg', 'modal-xl': size === 'xl'}">
        <div class="modal-content">
          <div class="modal-header" *ngIf="!hideHeader">
            <h5 class="modal-title">
              <ng-content select=".gp-modal-title"></ng-content>
              <a test-id="closeModal" *ngIf="closable" (click)="close()"
                 class="btn btn-icon pull-right text-muted modal-close-btn"
                 [ngClass]="{'btn-sm': size === 'lg', 'btn-md': size === 'xl', 'btn-xs': size === 'sm'}">
                <i aria-hidden="true" class="fa fa-light fa-times modal-close-icon"></i>
              </a>
            </h5>
          </div>
          <div class="modal-body p-lg">
            <a test-id="closeModal" *ngIf="closable && hideHeader" (click)="close()"
               class="btn btn-icon pull-right text-muted modal-close-btn"
               [ngClass]="{'btn-sm': size === 'lg', 'btn-md': size === 'xl', 'btn-xs': size === 'sm'}">
              <i aria-hidden="true" class="fa fa-light fa-times modal-close-icon"></i>
            </a>
            <ng-content select=".gp-modal-body"></ng-content>
          </div>
          <div class="modal-footer" *ngIf="!hideFooter">
            <!-- Custom footer -->
            <div #footer>
              <ng-content select=".gp-modal-footer"></ng-content>
            </div>
            <!-- Default footer fallback -->
            <div *ngIf="footer.children.length === 0">
              <div [attr.data-hidden]="state != states.NONE">
                <div class="btn-bar-wrapper">
                  <div class="btn-bar btn-bar-sm btn-bar-align-end">
                    <button type="button" *ngIf="submittable" class="btn btn-dark p-x-md" (click)="submit()"
                            [disabled]='!submitEnabled()'>
                      {{ 'FORMS.submit' | translate }}
                    </button>
                    <button type="button" *ngIf="cancellable" class="btn btn-secondary p-x-md" (click)="cancel()">
                      {{ 'FORMS.cancel' | translate }}
                    </button>
                  </div>
                </div>
              </div>
              <div [attr.data-hidden]="state != states.WAITING">
                <div class="pull-right">
                  <i aria-hidden="true" class="fa fa-light fa-spinner fa-spin"></i>
                </div>
              </div>
              <div [attr.data-hidden]="state != states.VALID">
                <div class="pull-right text-success">
                  <strong>{{ stateMessage }} <i aria-hidden="true" class="fa fa-light fa-check"></i></strong>
                </div>
              </div>
              <div [attr.data-hidden]="state != states.INVALID">
                <div class="pull-right text-danger">
                  <strong>{{ stateMessage }} <i aria-hidden="true" class="fa fa-light fa-exclamation-triangle"></i></strong>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  `
})
export class ModalComponent {

  /**
   * @type {string} One of 'lg', ...
   */
  @Input() public size: string = '';
  @Input() public closable: Boolean = false;
  @Input() public cancellable: Boolean = true;
  @Input() public submittable: Boolean = true;
  @Input() public form: Boolean = true;
  @Input() public hideHeader: Boolean = false;
  @Input() public hideFooter: Boolean = false;
  @Output() public closed: EventEmitter<void> = new EventEmitter<void>();

  public states: any = ModalState;
  public state: ModalState = ModalState.NONE;
  public stateMessage: string = '';

  @Input() public onSubmit: () => Promise<void> | Observable<ModalCommand> = () => Promise.resolve();
  @Input() public onCancel: () => Promise<void> | Observable<ModalCommand> = () => Promise.resolve();
  @Input() public submitEnabled: () => boolean = () => true;

  constructor(
    private modalService: ModalService,
    private ref: ChangeDetectorRef
  ) {
  }

  public submit() {
    this.state = ModalState.WAITING;
    const returned = this.onSubmit();

    if (returned instanceof Promise) {
      (<Promise<void>>returned).then(() => {
        this.state = ModalState.NONE;
        this.modalService.closeTopmostModal();
      });
    } else if (returned instanceof Observable) {
      (<Observable<ModalCommand>>returned).subscribe((command) => {
        this.state = command.state;
        this.stateMessage = command.message;

        if (command.duration) {
          setTimeout(() => {
            this.state = ModalState.NONE;
            if (command.state === ModalState.VALID) {
              this.modalService.closeTopmostModal();
              this.ref.markForCheck();
            }
          }, command.duration);
        } else if (command.state === ModalState.VALID) {
          this.modalService.closeTopmostModal();
        }
      });
    } else {
      console.warn('Callback passed to modal returns neither an observable, nor a promise');
    }
  }

  public cancel() {
    this.state = ModalState.WAITING;
    const returned = this.onCancel();
    if (returned instanceof Promise) {
      (<Promise<void>>returned).then(() => {
        this.state = ModalState.NONE;
        this.modalService.closeTopmostModal();
      });
    } else if (returned instanceof Observable) {
      (<Observable<ModalCommand>>returned).subscribe((command) => {
        this.state = command.state;
        this.stateMessage = command.message;
      }, error => {
        // FIXME do what, retry ?
      }, () => {
        this.state = ModalState.NONE;
        this.modalService.closeTopmostModal();
        this.closed.emit(undefined);
      });
    }
  }

  public close(event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    this.modalService.closeTopmostModal();
    this.closed.emit(undefined);
  }
}
